// Network (D81): the user's network lists — the domains their agents' containers may reach on
// top of the platform essentials — and making a new one.
import { Globe, Plus } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "rizzui";

import { useT } from "../i18n";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { networkListsQuery, type NetworkList } from "../network/api";
import { CreateListDrawer } from "../network/CreateListDrawer";
import { EssentialsCard } from "../network/EssentialsCard";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { type Column, DataTable } from "../ui/DataTable";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { ago } from "./format";

function columns(t: ReturnType<typeof useT>["network"]["lists"]): Column<NetworkList>[] {
  return [
    {
      key: "name",
      header: t.columns.name,
      sortValue: (l) => l.name.toLowerCase(),
      cell: (l) => (
        <div className="min-w-0">
          <Link to={`/network/${l.id}`} className="font-medium text-gray-900 hover:text-primary">
            {l.name}
          </Link>
          {l.description ? <div className="truncate text-[12.5px] text-gray-500">{l.description}</div> : null}
        </div>
      ),
    },
    { key: "domains", header: t.columns.domains, sortValue: (l) => l.domains.length, cell: (l) => t.domainCount(l.domains.length) },
    { key: "used", header: t.columns.usedBy, hideBelow: "sm", sortValue: (l) => l.used_by.length, cell: (l) => t.groupCount(l.used_by.length) },
    { key: "updated", header: t.columns.updated, hideBelow: "md", sortValue: (l) => l.updated_at, cell: (l) => <span className="whitespace-nowrap">{ago(l.updated_at)}</span> },
  ];
}

export default function NetworkListsPage() {
  const t = useT().network.lists;
  const lists = useQuery(networkListsQuery());
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader title={SECTIONS.network.title} breadcrumb={sectionCrumbs("network")} description={SECTIONS.network.subtitle}>
        <Button className="gap-1.5" onClick={() => setCreating(true)}>
          <Plus weight="bold" className="h-4 w-4" /> {t.create}
        </Button>
      </PageHeader>
      <div className="grid gap-5 xl:grid-cols-12">
        <div className={cn(cardClass, "min-w-0 p-3 lg:p-4 xl:col-span-8")}>
          {lists.isPending ? <Loading /> : null}
          {lists.isError ? <ErrorState title={t.loadFailed} message={lists.error.message} /> : null}
          {lists.data?.length === 0 ? (
            <EmptyState compact icon={<Globe weight="duotone" />} title={t.empty}>
              {t.emptyBody}
            </EmptyState>
          ) : null}
          {lists.data && lists.data.length > 0 ? <DataTable label={t.label} columns={columns(t)} rows={lists.data} rowKey={(l) => l.id} defaultSort={{ key: "name", dir: "asc" }} /> : null}
        </div>
        <EssentialsCard className="min-w-0 xl:col-span-4" />
      </div>
      {creating ? <CreateListDrawer open onClose={() => setCreating(false)} /> : null}
    </>
  );
}
